import { Injectable, Inject } from '@angular/core';
import { CVApiService } from '../services/cv-api.service';
import { cvAssignmentLists, cvAssignmentToolGroupItem, LoginApiModel } from './angular-assign-tool/angular-assign-tool.models';
import { ProfileApiModel } from 'src/shared/models/dashboard.model';

@Injectable({
    providedIn: 'root'
})
export class AngularAssignToolService {
    Loaded: boolean = false;

    constructor(
        public cvApiService: CVApiService,
    ) { }

    public async loadGroupTypes() {
        try {
            let response = await this.cvApiService.get<cvAssignmentLists>(`/api/v1/group_types`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error('Failed to load group types.');
        }
    }

    public async loadAttributes() {
        try {
            let response = await this.cvApiService.get<[]>(`/api/v1/attributes`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error('Failed to load attributes.');
        }
    }

    public async loadEmpGroups(level) {
        try {
            let response = await this.cvApiService.get<cvAssignmentToolGroupItem[]>(`/api/v1/emp_groups/${level}`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error('Failed to load emp groups.');
        }
    }

    public async loadRecGroups(level) {
        try {
            let response = await this.cvApiService.get<cvAssignmentToolGroupItem[]>(`/api/v1/rec_groups/${level}`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error('Failed to load rec groups.');
        }
    }

    public async loadAttributeValues(level) {
        try {
            let response = await this.cvApiService.get<[]>(`/api/v1/attribute_values/${level}`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error('Failed to load attribute values.');
        }
    }

    public async getGroupCount(level, id, type) {
        try {
            let response = await this.cvApiService.get<number>(`/api/v1/group_count/${level}/${id}/${type}`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error('Failed to load group count.');
        }
    }

    public async loadRoles() {
        try {
            const response = await this.cvApiService.get<ProfileApiModel[]>(`/api/v1/roles`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error('Failed to load roles.');
        }
    }

    /**
     * Loads the logins from one of three REST endpoints. If no parameters are provided, all accessible logins for the user
     * will be returned. If only a profileID is supplied, the list of logins that match the profile will be returned.
     * If all parameters are provided, the list of logins will be filtered to the given group and profile.
     * Either profileID, all parameters, or no parameters must be supplied.
     * 
     * @param {int? = null} groupLevel optional parameter to use as a filter for the RelatedGroupLevel
     * @param {int? = null} groupType optional parameter to use as a filter for the RelatedGroupType
     * @param {int? = null} groupID optional parameter to use as a filter for the RelatedGroupID
     * @param {int? = null} profileID optional parameter to use as a filter for the ProfileID
     */
    public async loadLogins(groupLevel, groupType, groupId, profileId) {
        let requestUrl = `/api/v1/logins`;
        if (groupLevel !== null && groupLevel !== undefined
            && groupType !== null && groupType !== undefined
            && groupId !== null && groupId !== undefined
            && profileId !== null && profileId !== undefined) {
            requestUrl += `/level/${groupLevel}/type/${groupType}/group/${groupId}/profile/${profileId}`;
        }
        else if (profileId !== null && profileId !== undefined)
            requestUrl += `/profile/${profileId}`;

        try {
            let response = await this.cvApiService.get<LoginApiModel[]>(requestUrl);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error('Failed to load logins.');
        }
    }

    /**
     * Loads the logins based on parent group selected
     * This was created for Budget Notifications to get the logins based on the parent group assigned
     * 
     * @param {int? = null} groupLevel parameter to use as a filter for the ParentGroupLevel
     * @param {int? = null} groupType parameter to use as a filter for the ParentGroupType
     * @param {int? = null} groupID parameter to use as a filter for the ParentGroupID
     */
    public async loadLoginsByParentGroup(groupLevel, groupType, groupId) {
        try {
            let response = await this.cvApiService.get<LoginApiModel[]>(`/api/v1/logins/level/${groupLevel}/type/${groupType}/parentGroup/${groupId}`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error('Failed to load logins by parent group.');
        }
    }

    /**
    * Load logins that have access to the MarketPlaceManagement page, 
    * to be assigned to receibe notifications when a new order is submited
    */
    public async loadPointStoreLogins() {
        try {
            let response = await this.cvApiService.get<LoginApiModel[]>(`/api/v1/pointstore_logins`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error('Failed to load point store logins.');
        }
    }

    /**
     * Makes a REST endpoint request to share a dashboard with a profile in a given group.
     * 
     * @param {string} dashboardKey
     * @param {int} companyID
     * @param {int} groupID
     * @param {int} groupLevel
     * @param {int} groupType The integer group type corresponding to the BaseGroupType enum.
     * @param {int} profileID
     * @param {boolean} read
     * @param {boolean} write
     */
    public async shareDashboardToProfile(dashboardKey, companyId, groupId, groupLevel, groupType, profileId, read, write, template) {
        let groupTypeStr = "";
        switch (groupType) {
            case 0:
                groupTypeStr = "emp";
                break;
            case 1:
                groupTypeStr = "rec";
                break;
            case 2:
                groupTypeStr = "att";
                break;
        }

        try {
            let response = await this.cvApiService.post(`/api/v2/shared-dashboard/${dashboardKey}/company/${companyId}/group/${groupId}/level/${groupLevel}/type/${groupTypeStr}/profile/${profileId}/read/${read}/write/${write}/template/${template}`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error('Failed to share dashboard to profile.');
        }
    }

    /**
     * Makes a REST endpoint request to share a dashboard with a login.
     * 
     * @param {string} dashboardKey
     * @param {int} loginID
     * @param {boolean} read
     * @param {boolean} write
     */
    public async shareDashboardToLogin(dashboardKey, loginId, read, write, template) {
        try {
            let response = await this.cvApiService.post(`/api/v2/shared-dashboard/${dashboardKey}/login/${loginId}/read/${read}/write/${write}/template/${template}`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error('Failed to share dashboard to login.');
        }
    }

    /**
     * Makes a REST endpoint request to remove all shares for the given dashboard.
     * 
     * @param {string} dashboardKey
     */
    public async resetDashboardShare(dashboardKey) {
        try {
            let response = await this.cvApiService.delete(`/api/v2/dashboard-shares/${dashboardKey}`);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error('Failed to reset dashboard share.');
        }
    }

    public async saveWallboardChanges(wallboardId, dataList) {
        try {
            let response = await this.cvApiService.post(`/api/v2/updateWallboardAssignments/${wallboardId}`, dataList);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error('Failed to share dashboard to login.');
        }
    }
}
